<template>
  <div class="p-grid">
    <div class="p-col-12">
      <Card v-bind="$attrs">
        <template #title="">
          <div class="p-d-flex p-justify-between">
            แบบประเมินพนักงานใหม่/พนักงานย้ายแผนก/พนักงานได้เลื่อนตำแหน่ง
            <div>
              <CancelButton
                @cancel="onCancelDocument"
                v-if="isAdmin() && !positionDocument.cancelOn"
              />
              <BackButton class="p-ml-2"></BackButton>
            </div>
          </div>
        </template>
        <template #subtitle="">
          <div>
            <StatusBadge
              :approveOn="positionDocument.approveOn"
              :submitOn="positionDocument.submitOn"
              :status="positionDocument.status"
            />
          </div>
        </template>
        <template #content="">
          <div class="p-grid">
            <div class="p-col-12">
              <b>เลขที่เอกสาร</b> {{ positionDocument.id }}
            </div>
            <div class="p-col-12 p-text-bold" style="fontSize: 1.2rem">
              ข้อมูลผู้เข้าร่วม
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>วันที่เอกสาร :</b>
              {{ formatAppDate(positionDocument.documentDate) }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>ชื่อ :</b>
              {{ positionDocument.employee?.prename
              }}{{ positionDocument.employee?.name }}
              {{ positionDocument.employee?.surname }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>ตำแหน่ง :</b>
              {{ positionDocument.employee?.position }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>รหัส :</b>
              {{ positionDocument.employee?.id }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>แผนก/ฝ่าย :</b>
              {{ positionDocument.employee?.divisionDescription ?? "-" }} /
              {{ positionDocument.employee?.departmentDescription ?? "-" }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>วันที่เริ่มงาน :</b>
              {{ formatAppDate(positionDocument.employee?.startDate) }}
            </div>
            <div class="p-col-12">
              <PositionDocumentPanel
                :isSupervisor="isSupervisor"
                :isEmployee="isEmployee"
                :isReadOnly="isReadonly"
                v-model:assessmentData="positionDocument.assessmentData"
                v-model:supervisorAssessmentData="
                  positionDocument.supervisorAssessmentData
                "
                v-model:isComplete="isSupervisorComplete"
              />
            </div>
            <div class="p-col-12" v-if="positionDocument.questionId">
              <QuestionPanel
                v-if="isEmployee"
                :questionItems="questionItems"
                v-model:assessmentItems="questionAssessmentItems"
                :readonly="isReadonly"
                :isSupervisor="isSupervisor"
                :isEmployee="isEmployee"
                v-model:isComplete="isQuestionComplete"
              ></QuestionPanel>
              <QuestionPanel
                v-else
                :questionItems="questionItems"
                v-model:assessmentItems="questionAssessmentItems"
                :readonly="isReadonly"
                :isSupervisor="isSupervisor"
                :isEmployee="isEmployee"
                v-model:isComplete="isQuestionComplete"
              ></QuestionPanel>
            </div>
          </div>
        </template>
        <template #footer="">
          <div class="p-d-flex">
            <Button
              icon="pi pi-save"
              label="บันทึกข้อมูล"
              @click="onSaveDocument"
              :disabled="isReadonly"
            ></Button>
            <span class="p-float-label p-ml-2">
              <InputUser
                id="supervisorId"
                v-model="positionDocument.supervisorId"
                style="width: 300px"
                disabled
                :class="{
                  'p-invalid': v$.positionDocument.supervisorId.$error,
                }"
              ></InputUser>
              <small
                class="p-error"
                v-if="v$.positionDocument.supervisorId.$error"
                >{{
                  v$.positionDocument.supervisorId.requiredIf.$message
                }}</small
              >
              <label for="supervisorId">ระบุหัวหน้างาน</label>
            </span>
            <Button
              class="p-ml-2 p-button-danger"
              icon="pi pi-send"
              label="ส่งเอกสาร"
              @click="onSubmitDocument"
              v-if="isEmployee === true"
              :disabled="
                isReadonly ||
                  (!isQuestionComplete && positionDocument.questionId)
              "
            ></Button>
            <Button
              class="p-ml-2 p-button-danger"
              icon="pi pi-send"
              label="อนุมัติเอกสาร"
              @click="onApproveDocument"
              v-if="isSupervisor === true"
              :disabled="
                isReadonly ||
                  !isSupervisorComplete ||
                  (!isQuestionComplete && positionDocument.questionId)
              "
            ></Button>
          </div>
        </template>
      </Card>
    </div>
    <div class="p-col-12">
      <Card>
        <template #subtitle="">
          เอกสารแนบ
        </template>
        <template #content="">
          <div class="p-grid">
            <div class="p-col-12">
              <AttachmentPanel
                :documentId="positionDocument.id"
              ></AttachmentPanel>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { usePlugin } from "@/plugins";
import {
  PositionDocument,
  PositionDocumentItem,
  GetPositionDocumentRequest,
  GetPositionDocumentResponse,
  UpsertPositionDocumentRequest,
  AssessmentItem,
  CancelTrainingDocumentRequest,
  CancelPositionDocumentRequest,
} from "@/types";
import BackButton from "@/components/BackButton.vue";
import InputUser from "@/components/InputUser.vue";
import PositionDocumentPanel from "@/components/PositionDocumentPanel.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import CancelButton from "@/components/CancelButton.vue";
import AttachmentPanel from "@/components/AttachmentPanel.vue";
import QuestionPanel from "@/components/QuestionPanel.vue";
import { useVuelidate } from "@vuelidate/core";
import { requiredIf } from "@vuelidate/validators";

export default defineComponent({
  name: "PositionDocumentAssessment",
  components: {
    BackButton,
    InputUser,
    PositionDocumentPanel,
    StatusBadge,
    AttachmentPanel,
    QuestionPanel,
    CancelButton,
  },
  setup() {
    const initialData: PositionDocument = {
      id: "",
      releaseOn: null,
      releaseById: null,
      releaseBy: null,
      dunningDate1: null,
      dunningDate2: null,
      isDunning1: false,
      isDunning2: false,
      superVisorDunningDate1: null,
      superVisorDunningDate2: null,
      isSuperVisorDunning1: false,
      isSuperVisorDunning2: false,
      documentDate: null,
      submitOn: null,
      approveOn: null,
      employeeId: null,
      supervisorId: null,
      type: 0,
      questionId: 0,
      assessmentData: null,
      supervisorAssessmentData: null,
      employee: null,
      supervisor: null,
      question: null,
      status: "",
      questionData: null,
      supervisorQuestionData: null,
      cancelOn: null,
      cancelById: null,
      cancelBy: null,
      releaseStatus: null,
    };
    const {
      setLoading,
      error,
      success,
      route,
      axios,
      dayjs,
      formatAppDate,
      useState,
      confirmation,
      isAdmin,
    } = usePlugin();
    const positionDocument = ref<PositionDocument>(initialData);
    const items = ref<PositionDocumentItem[]>([]);
    const questionItems = computed(() =>
      JSON.parse(positionDocument.value.question?.body ?? "[]")
    );
    const questionAssessmentItems = ref<AssessmentItem[]>([]);

    const id = computed(() => route.params["id"] as string);

    const isQuestionComplete = ref(false);
    const isSubmitting = ref(true);
    const rules = computed(() => {
      return {
        positionDocument: {
          supervisorId: {
            requiredIf: requiredIf(() => isSubmitting.value),
          },
        },
      };
    });
    const isSupervisorComplete = ref(false);
    const v$ = useVuelidate(rules, { positionDocument });

    const loadData = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetPositionDocumentResponse>(
          "api/v1/services/positionDocument/get",
          {
            id: id.value,
          } as GetPositionDocumentRequest
        );
        positionDocument.value = result.data.positionDocuments[0];

        //setup supervisor
        if (
          !positionDocument.value.supervisorAssessmentData &&
          isSupervisor.value === true
        ) {
          positionDocument.value.supervisorAssessmentData =
            positionDocument.value.assessmentData;
        }
        if (
          !positionDocument.value.supervisorQuestionData &&
          isSupervisor.value === true
        ) {
          positionDocument.value.supervisorQuestionData =
            positionDocument.value.questionData;
        }

        //Setup question assessment if any.
        if (positionDocument.value.questionData && isEmployee.value === true) {
          questionAssessmentItems.value = JSON.parse(
            positionDocument.value.questionData
          );
        }
        //setup question data for supervisor.
        if (
          positionDocument.value.supervisorQuestionData &&
          isSupervisor.value === true
        ) {
          questionAssessmentItems.value = JSON.parse(
            positionDocument.value.supervisorQuestionData
          );
        }
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const onSaveDocument = async () => {
      isSubmitting.value = false;
      v$.value.$reset();
      v$.value.$touch();
      if (!(await v$.value.$validate())) return;

      setLoading(true);
      try {
        if (questionAssessmentItems.value) {
          if (isEmployee.value) {
            positionDocument.value.questionData = JSON.stringify(
              questionAssessmentItems.value
            );
          }
          if (isSupervisor.value) {
            positionDocument.value.supervisorQuestionData = JSON.stringify(
              questionAssessmentItems.value
            );
          }
        }
        const result = await axios.post(
          "api/v1/services/positionDocument/upsert",
          {
            positionDocument: positionDocument.value,
          } as UpsertPositionDocumentRequest
        );
        success("บันทึกข้อมูลเรียบร้อยแล้ว");
        loadData();
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const onSubmitDocument = async () => {
      isSubmitting.value = true;
      v$.value.$reset();
      v$.value.$touch();
      if (!(await v$.value.$validate())) return;
      confirmation.require({
        header: "ยืนยันส่งเอกสาร",
        message: `กรุณายืนยันเพื่อทำการส่งเอกสารให้หัวหน้างานรหัส ${positionDocument.value.supervisorId}`,
        acceptLabel: "ยืนยัน",
        accept: async () => {
          positionDocument.value.submitOn = dayjs().toDate();
          await onSaveDocument();
        },
        rejectLabel: "ยกเลิก",
      });
    };
    const onApproveDocument = async () => {
      isSubmitting.value = false;
      v$.value.$reset();
      v$.value.$touch();
      if (!(await v$.value.$validate())) return;

      confirmation.require({
        header: "อนุมัติเอกสาร",
        message: `กรุณายืนยันเพื่อทำการอนุมัติเอกสารและส่งให้ HRD?`,
        acceptLabel: "ยืนยัน",
        accept: async () => {
          positionDocument.value.approveOn = dayjs().toDate();
          await onSaveDocument();
        },
        rejectLabel: "ยกเลิก",
      });
    };

    const state = useState();

    const isReadonly = computed(() => {
      const username = state.value.authentication.username;
      if (
        positionDocument.value.employeeId === username &&
        positionDocument.value !== null &&
        positionDocument.value.submitOn === null
      )
        return false;
      if (
        positionDocument.value !== null &&
        positionDocument.value.supervisorId === username &&
        positionDocument.value.submitOn !== null &&
        positionDocument.value.approveOn === null
      )
        return false;
      return true;
    });

    const isSupervisor = computed(
      () =>
        state.value.authentication.username ===
        positionDocument.value.supervisorId
    );
    const isEmployee = computed(
      () =>
        state.value.authentication.username ===
        positionDocument.value.employeeId
    );

    const onCancelDocument = async () => {
      setLoading(true);
      try {
        await axios.post("api/v1/services/positionDocument/cancel", {
          id: positionDocument.value.id,
        } as CancelPositionDocumentRequest);
        success("ยกเลิกเอกสารเรียบร้อยแล้ว");
        loadData();
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    onMounted(() => loadData());

    return {
      positionDocument,
      items,

      onSaveDocument,
      onSubmitDocument,
      onApproveDocument,
      onCancelDocument,

      formatAppDate,
      isSupervisor,
      isEmployee,
      isReadonly,
      isAdmin,
      isSupervisorComplete,
      isQuestionComplete,

      questionItems,
      questionAssessmentItems,

      v$,
      rules,
    };
  },
});
</script>

<style></style>
